import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, GripVertical, Trash2, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { currentUserUuid } from "@/shared/auth/me.js";
import { SIGNER_EMAIL_MAX, SIGNER_NAME_MAX, isValidPinfl, isValidSignerEmail } from "@/modules/my-docs/hooks/useSignerList.js";

/**
 * One signer in the unified picker.
 *
 * A row is either: (a) a person of the install picked from the dropdown
 * (`GET /embed/v1/workflow/users-with-certs`: `peopleWithCerts`), signing
 * with one of their registered certs, or (b) an external signer (no
 * `user_uuid`, owner types the PINFL directly).
 *
 * The row: `{user_uuid, certificate_uuid, national_id_value, full_name,
 * email}` — a participant of the request as is. Registered certs come masked
 * (users-with-certs never lists a national ID): a row pins one by
 * `certificate_uuid` and the backend fills its PINFL. One cert is picked by
 * itself; with 2+ the owner picks a chip below the input. The PINFL input
 * stays editable: typing one pins a cert the user hasn't registered yet
 * instead.
 */
export const SignerRow = ({
  row,
  index,
  signingMode,
  peopleWithCerts = [],
  loadingUsers,
  canRemove,
  onChange,
  onRemove,
  onDragStart,
  onDragEnd,
}) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const me = currentUserUuid();
  const pickedUser = useMemo(() => {
    if (row.user_uuid == null) return null;
    return peopleWithCerts.find((u) => String(u.uuid) === String(row.user_uuid)) || null;
  }, [row.user_uuid, peopleWithCerts]);

  const userCerts = pickedUser?.certificates || [];

  const certMaskOf = (c) => c?.nationalIdMasked || c?.national_id_masked || "";
  const certNameOf = (c) => c?.subjectCommonName || c?.subject_common_name || "";
  const pickedCert = userCerts.find((c) => c.uuid === row.certificate_uuid) || null;

  const handlePickUser = (user) => {
    const certs = user?.certificates || [];
    onChange({
      ...row,
      user_uuid: user.uuid,
      // Picking (or re-picking) a user refills their details. Their single
      // cert is the one they sign with; with multiple certs nothing is pinned
      // so the chips below prompt a choice.
      certificate_uuid: certs.length === 1 ? certs[0].uuid : null,
      national_id_value: "",
      full_name: user.displayName || user.display_name || user.email || "",
      email: user.email || "",
    });
    setPickerOpen(false);
  };

  const handleClearUser = () => {
    onChange({ ...row, user_uuid: null, certificate_uuid: null });
  };

  const handleFieldChange = (field, value) => {
    onChange({ ...row, [field]: value });
  };

  // a typed PINFL replaces the picked cert; a picked cert replaces the typed PINFL
  const handlePinflChange = (value) => onChange({ ...row, national_id_value: value, certificate_uuid: null });
  const handlePickCert = (cert) => onChange({ ...row, certificate_uuid: cert.uuid, national_id_value: "" });

  const pinflValid = !row.national_id_value || isValidPinfl(row.national_id_value);
  const emailValid = isValidSignerEmail(row.email);

  const userLabel = pickedUser
    ? pickedUser.displayName || pickedUser.display_name || pickedUser.email
    : t("signing:addSigners.pickUser");

  return (
    <div
      className={cn(
        "relative flex flex-col gap-3 p-4 rounded-md border border-border bg-background/50 transition-all hover:border-primary/30",
        signingMode === "sequential" && "pl-11",
      )}
    >
      {signingMode === "sequential" && (
        <div
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="absolute left-3 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 select-none cursor-grab active:cursor-grabbing"
          title={t("signing:addSigners.dragHint")}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground/40" />
          <span className="text-[11px] font-bold text-muted-foreground bg-muted w-5 h-5 rounded-full flex items-center justify-center border border-border">
            {index + 1}
          </span>
        </div>
      )}

      <div className="flex items-end gap-3 pr-8">
        {/* PINFL */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            {t("signing:addSigners.pinflLabel")}
          </label>
          <Input
            placeholder={certMaskOf(pickedCert) || t("signing:addSigners.pinflPlaceholder")}
            value={row.national_id_value || ""}
            // a PINFL is 14 digits: anything else never reaches the row
            onChange={(e) => handlePinflChange(e.target.value.replace(/\D/g, "").slice(0, 14))}
            inputMode="numeric"
            maxLength={14}
            aria-invalid={!pinflValid || undefined}
            className={cn(
              "h-10",
              !pinflValid && "border-red-500 ring-1 ring-red-500/20",
            )}
          />
        </div>

        {/* User picker */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            {t("signing:addSigners.userLabel")}
          </label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className={cn(
                  "w-full justify-between h-10 font-normal",
                  !pickedUser && "text-muted-foreground",
                )}
              >
                <span className="truncate">{userLabel}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
              <Command>
                <CommandInput placeholder={t("signing:mode.searchPlaceholder")} />
                <CommandList>
                  {loadingUsers ? (
                    <div className="py-6 text-center text-xs text-muted-foreground">
                      {t("signing:mode.loading")}
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>
                        {t("signing:mode.noMatchingUsers")}
                      </CommandEmpty>
                      <CommandGroup>
                        {pickedUser && (
                          <CommandItem
                            key="__clear"
                            value="__clear"
                            onSelect={() => {
                              handleClearUser();
                              setPickerOpen(false);
                            }}
                            className="text-muted-foreground"
                          >
                            <UserPlus className="mr-2 h-4 w-4 rotate-45" />
                            {t("signing:addSigners.clearUser")}
                          </CommandItem>
                        )}
                        {peopleWithCerts.map((u) => {
                          const isSelected = String(u.uuid) === String(row.user_uuid);
                          const isSelf = me != null && String(u.uuid) === String(me);
                          const certCount = (u.certificates || []).length;
                          return (
                            <CommandItem
                              key={u.uuid}
                              value={`${u.email || ""} ${u.displayName || u.display_name || ""}`}
                              onSelect={() => handlePickUser(u)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  isSelected ? "opacity-100" : "opacity-0",
                                )}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm truncate">
                                  {u.displayName || u.display_name || u.email}
                                  {isSelf && (
                                    <span className="ml-2 text-[10px] text-muted-foreground">
                                      {t("signing:mode.youInitiator")}
                                    </span>
                                  )}
                                </p>
                                {(u.displayName || u.display_name) && u.email && (
                                  <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                                )}
                              </div>
                              <span
                                className={cn(
                                  "ml-2 text-[10px] px-1.5 py-0.5 rounded-full border",
                                  certCount === 0
                                    ? "bg-amber-50 border-amber-200 text-amber-700"
                                    : "bg-emerald-50 border-emerald-200 text-emerald-700",
                                )}
                                title={certCount === 0 ? t("signing:addSigners.noCertHint") : ""}
                              >
                                {certCount === 0
                                  ? t("signing:addSigners.noCert")
                                  : t("signing:addSigners.certCount", { count: certCount })}
                              </span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex-1 min-w-0 space-y-1.5">
          <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            {t("signing:addSigners.nameLabel")}
          </label>
          <Input
            placeholder={t("signing:addSigners.namePlaceholder")}
            value={row.full_name || ""}
            maxLength={SIGNER_NAME_MAX}
            // Names: letters (with their marks), numbers, spaces and the
            // apostrophes, hyphens and dots names carry (O‘ktam, Abdulla-o‘g‘li).
            onChange={(e) => handleFieldChange("full_name", e.target.value.replace(/[^\p{L}\p{M}\p{N} '‘’ʻʼ.-]/gu, ""))}
            className="h-10"
          />
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            {t("signing:addSigners.emailLabel")}
          </label>
          <Input
            placeholder={t("signing:addSigners.emailPlaceholder")}
            type="email"
            value={row.email || ""}
            maxLength={SIGNER_EMAIL_MAX}
            aria-invalid={!emailValid || undefined}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            className="h-10"
          />
        </div>
      </div>

      {/* Inline help below the row.
          Chips only appear when there's something useful to offer:
            • multi-cert: list each option so the owner can switch
            • single-cert that doesn't match the current PINFL: offer revert
          A single cert that already matches → no chip (input already shows it). */}
      {((userCerts.length > 1 || (userCerts.length === 1 && !pickedCert)) || (!pinflValid && row.national_id_value)) && (
        <div className="flex flex-wrap items-center gap-1.5 -mt-1">
          {(userCerts.length > 1 || (userCerts.length === 1 && !pickedCert)) &&
            userCerts.map((cert) => {
              const certMask = certMaskOf(cert);
              const isCurrent = cert.uuid === row.certificate_uuid;
              const label = certNameOf(cert) || certMask;
              if (!label) return null;
              return (
                <button
                  key={cert.uuid}
                  type="button"
                  onClick={() => handlePickCert(cert)}
                  className={cn(
                    "inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors",
                    isCurrent
                      ? "bg-primary/10 border-primary/30 text-primary"
                      : "bg-muted border-border text-muted-foreground hover:bg-primary/5 hover:border-primary/20",
                  )}
                  title={certMask}
                >
                  {isCurrent && <Check className="w-2.5 h-2.5" />}
                  <span className="truncate max-w-[200px]">{label}</span>
                </button>
              );
            })}
          {!pinflValid && row.national_id_value && (
            <p className="text-[10px] text-red-500 ml-2">
              {t("signing:addSigners.invalidPinflHint")}
            </p>
          )}
        </div>
      )}
      {!emailValid && (
        <p className="text-[10px] text-red-500 -mt-1" role="alert" data-testid="signer-email-error">
          {t("signing:addSigners.invalidEmail")}
        </p>
      )}

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 p-1.5 rounded-md text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 transition-colors"
          aria-label={t("signing:addSigners.removeSigner")}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default SignerRow;
